import { useState, useEffect, useCallback } from 'react';
import { isApiError } from '@aiptyg/types';
import * as assignmentsService from '../services/assignments.service';
import type { AssignmentDetailDTO, DownloadStatsDTO, MessageReviewDetailDTO } from '../types/assignments.types';
import { useToastStore } from '../../../store/toast.store';

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      assignment_not_found: '审核记录不存在',
      message_review_not_found: '短信证据审核记录不存在',
      message_review_not_ready: '短信证据审核尚未就绪',
      message_review_conflict: '当前短信证据状态不允许该操作',
      message_review_override_required: '需要独立人工复核权限',
      message_review_fail_candidate_reject_only: '当前短信证据状态不允许该操作',
      message_retry_invalid: '重试原因不完整',
      message_replace_invalid: '更换目标参数不完整',
      network_error: '网络连接失败，请检查网络',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

export function useAssignmentDetail(id: number) {
  const [assignment, setAssignment] = useState<AssignmentDetailDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [messageReview, setMessageReview] = useState<MessageReviewDetailDTO | null>(null);
  const [messageReviewLoading, setMessageReviewLoading] = useState(false);
  const [messageReviewError, setMessageReviewError] = useState<string | null>(null);
  const [messageActionLoading, setMessageActionLoading] = useState(false);
  const addToast = useToastStore((s) => s.addToast);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setMessageReview(null);
    setMessageReviewError(null);
    setMessageReviewLoading(false);
    try {
      const result = await assignmentsService.getAssignment(id);
      setAssignment(result);
      if (result.task_type === 'message_send') {
        setMessageReviewLoading(true);
        try {
          const detail = await assignmentsService.getMessageReviewDetail(id);
          setMessageReview(detail);
        } catch (e) {
          setMessageReviewError(resolveError(e));
        } finally {
          setMessageReviewLoading(false);
        }
      }
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const refreshImages = useCallback(async () => {
    try { setMessageReview(await assignmentsService.getMessageReviewDetail(id)); }
    catch { addToast('error', '图片链接刷新失败，请重试'); }
  }, [id, addToast]);

  useEffect(() => {
    if (assignment?.task_type !== 'message_send') return;
    let refreshedAt = Date.now();
    const refresh = () => {
      if (document.visibilityState === 'visible' && Date.now() - refreshedAt >= 240_000) {
        refreshedAt = Date.now();
        void refreshImages();
      }
    };
    const timer = window.setInterval(refresh, 30_000);
    window.addEventListener('focus', refresh);
    return () => { window.clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [assignment?.task_type, refreshImages]);

  const review = useCallback(async (
    action: 'approve' | 'reject',
    note: string,
    auditReason?: string,
  ) => {
    setReviewing(true);
    try {
      if (assignment?.task_type === 'message_send') {
        await assignmentsService.reviewMessageAssignment(id, action, note, auditReason || note);
      } else {
        await assignmentsService.reviewAssignment(id, action, note, auditReason);
      }
      addToast('success', action === 'approve' ? '审核通过' : '已拒绝');
      load();
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setReviewing(false);
    }
  }, [id, addToast, assignment?.task_type, load]);

  const retryMessageReview = useCallback(async (auditReason: string) => {
    setMessageActionLoading(true);
    try {
      await assignmentsService.retryMessageReview(id, auditReason);
      addToast('success', '已重新加入审核队列');
      load();
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setMessageActionLoading(false);
    }
  }, [id, addToast, load]);

  const replaceMessageItem = useCallback(async (newItemId: number, auditReason: string) => {
    setMessageActionLoading(true);
    try {
      await assignmentsService.replaceMessageItem(id, newItemId, auditReason);
      addToast('success', '已更换目标');
      load();
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setMessageActionLoading(false);
    }
  }, [id, addToast, load]);

  return {
    refreshImages,
    assignment,
    loading,
    error,
    reviewing,
    reload: load,
    review,
    messageReview,
    messageReviewLoading,
    messageReviewError,
    messageActionLoading,
    retryMessageReview,
    replaceMessageItem,
  };
}

export function useDownloadStats(taskId: number | undefined) {
  const [stats, setStats] = useState<DownloadStatsDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await assignmentsService.getDownloadStats(taskId);
      setStats(result);
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    load();
  }, [load]);

  return { stats, loading, error, reload: load };
}
